// DialogHost — renders the active in-app dialog from dialogService.
// Mounted ONCE at the app root (main.jsx). Renders null until a dialog is queued,
// so it is invisible in normal use. See services/dialogService.ts for the queue.
//
// Kinds: confirm / alert (buttons only) and prompt (a multi-line text input that
// resolves the typed string, or null on cancel/dismiss). A prompt can be draggable.

import React from "react";
import { respondToDialog, subscribeDialogs } from "../../services/dialogService";
import { Btn } from "../../utils/ui.jsx";
import { ModalBackdrop } from "../form/FormUI.jsx";

// A prompt dialog: draggable modal with a textarea. Its own component so the text
// state resets cleanly per prompt (keyed on the dialog id by the caller).
function PromptDialog({ dialog }) {
  const [text, setText] = React.useState("");
  const headerRef = React.useRef(null);
  const inputRef = React.useRef(null);
  // Focus the textarea when the prompt opens (a11y-friendly alternative to autoFocus).
  React.useEffect(function () {
    inputRef.current?.focus();
  }, []);
  function respond(value) {
    respondToDialog(dialog.id, value);
  }
  // A minLength makes the prompt MANDATORY: no cancel button, no backdrop dismiss,
  // and the affirmative button stays disabled until enough text is entered.
  const minLength = dialog.minLength || 0;
  const mandatory = minLength > 0;
  const trimmedLen = text.trim().length;
  const canSubmit = trimmedLen >= minLength;
  const shortfall = minLength - trimmedLen;
  return (
    <ModalBackdrop
      portal
      zIndex={99999}
      draggable={dialog.draggable}
      dragHandleRef={headerRef}
      onClose={function () {
        // Mandatory prompts can't be dismissed without answering.
        if (mandatory) return;
        respond(null);
      }}
      contentStyle={{
        background: "#FFFFFF",
        borderRadius: 12,
        width: "calc(100vw - 40px)",
        maxWidth: 520,
        boxShadow: "0 8px 40px rgba(0,0,0,0.25)",
        overflow: "hidden",
      }}
    >
      <div
        ref={headerRef}
        style={{
          background: "#00695C",
          color: "white",
          padding: "12px 18px",
          fontWeight: 700,
          fontSize: 15,
          cursor: dialog.draggable ? "move" : "default",
        }}
      >
        {dialog.title || "Add a message"}
      </div>
      <div className="p-[18px]">
        {dialog.message ? (
          <div className="text-[13px] text-zephyr-muted mb-3 whitespace-pre-line">
            {dialog.message}
          </div>
        ) : null}
        <textarea
          ref={inputRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={dialog.placeholder || ""}
          rows={4}
          className="w-full py-[10px] px-[12px] rounded-[8px] border border-zephyr-border text-[13px] resize-y box-border text-zephyr-text bg-white leading-[1.5]"
          style={{ fontFamily: "inherit" }}
        />
        {mandatory && !canSubmit ? (
          <div className="text-[12px] text-zephyr-muted mt-[6px]">
            Please enter at least {minLength} characters
            {trimmedLen > 0 ? ` (${shortfall} more)` : ""}.
          </div>
        ) : null}
        <div className="flex gap-[10px] justify-end mt-[14px]">
          {mandatory ? null : (
            <Btn
              variant="secondary"
              onClick={function () {
                respond(null);
              }}
            >
              {dialog.cancelLabel || "Cancel"}
            </Btn>
          )}
          <Btn
            disabled={!canSubmit}
            onClick={function () {
              if (!canSubmit) return;
              respond(text);
            }}
          >
            {dialog.confirmLabel || "OK"}
          </Btn>
        </div>
      </div>
    </ModalBackdrop>
  );
}

export function DialogHost() {
  const [active, setActive] = React.useState(null);

  React.useEffect(function () {
    // subscribeDialogs fires immediately with the current value, then on changes.
    return subscribeDialogs(setActive);
  }, []);

  if (!active) return null;

  if (active.kind === "prompt") return <PromptDialog key={active.id} dialog={active} />;

  const isConfirm = active.kind === "confirm";

  function respond(value) {
    respondToDialog(active.id, value);
  }

  return (
    <ModalBackdrop
      // Render via a portal at a z-index ABOVE every other modal so a confirm/alert
      // triggered from inside a modal or full-screen overlay (e.g. the caseworker
      // edit form's "Password Reset Email") always appears on top and is clickable —
      // otherwise it sits behind the modal and looks like "nothing happened".
      portal
      zIndex={99999}
      onClose={function () {
        // Backdrop click / Escape = cancel a confirm, dismiss an alert.
        respond(false);
      }}
      contentStyle={{
        background: "#FFFFFF",
        borderRadius: 14,
        padding: 24,
        maxWidth: 420,
        width: "calc(100vw - 40px)",
        boxShadow: "0 8px 40px rgba(0,0,0,0.2)",
      }}
    >
      {active.title ? <div className="font-bold text-base mb-[10px]">{active.title}</div> : null}
      <div className="text-[13px] text-zephyr-muted mb-5 whitespace-pre-line">{active.message}</div>
      <div className="flex gap-[10px] justify-end">
        {isConfirm ? (
          <Btn
            variant="secondary"
            onClick={function () {
              respond(false);
            }}
          >
            {active.cancelLabel || "Cancel"}
          </Btn>
        ) : null}
        <Btn
          variant={active.danger ? "danger" : "primary"}
          onClick={function () {
            respond(true);
          }}
        >
          {active.confirmLabel || (isConfirm ? "Confirm" : "OK")}
        </Btn>
      </div>
    </ModalBackdrop>
  );
}
